/**
 * Custom cursor for fine pointers.
 * Lead dot is 12px. Trail dots are a shuffled mix of 2–10px, mostly small.
 * Every dot stays blurred by 6px. Hover and click grow each dot by 8px.
 * Touch and reduced-motion keep the system cursor.
 */
(() => {
  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  if (!finePointer.matches || reduceMotion.matches) return;

  function shuffle(list) {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const swap = list[i];
      list[i] = list[j];
      list[j] = swap;
    }
    return list;
  }

  const trailSizes = shuffle([
    2, 2, 2, 2, 2, 2, 2, 2,
    3, 3, 3, 3, 3, 3,
    4, 4, 4, 4,
    5, 5, 5,
    6, 6,
    7, 7,
    8, 9, 10,
  ]);
  const hoverSelector = [
    "a[href]",
    "button:not(:disabled)",
    "input:not(:disabled):not([type='hidden'])",
    "textarea:not(:disabled)",
    "select:not(:disabled)",
    "summary",
    "label",
    "[role='button']:not([aria-disabled='true'])",
    "[data-shop-card]",
    "[data-panel-gallery]",
  ].join(", ");

  const root = document.createElement("div");
  root.className = "cursor";
  root.setAttribute("aria-hidden", "true");

  const dots = trailSizes.map((size) => {
    const el = document.createElement("span");
    el.className = "cursor__dot";
    el.style.setProperty("--cursor-size", `${size}px`);
    root.appendChild(el);
    return { el, x: 0, y: 0 };
  });

  const leadEl = document.createElement("span");
  leadEl.className = "cursor__dot cursor__dot--lead";
  root.appendChild(leadEl);
  const lead = { el: leadEl, x: 0, y: 0 };

  document.body.appendChild(root);

  let mx = 0;
  let my = 0;
  let active = false;
  let rafId = 0;

  function place(dot) {
    dot.el.style.transform = `translate3d(${dot.x}px, ${dot.y}px, 0) translate(-50%, -50%)`;
  }

  function tick() {
    if (!active) {
      rafId = 0;
      return;
    }

    lead.x = mx;
    lead.y = my;
    place(lead);

    const maxGap = 32;
    let prev = lead;
    for (let i = 0; i < dots.length; i++) {
      const dot = dots[i];
      dot.x += (prev.x - dot.x) * 0.32;
      dot.y += (prev.y - dot.y) * 0.32;
      const dx = prev.x - dot.x;
      const dy = prev.y - dot.y;
      const dist = Math.hypot(dx, dy);
      if (dist > maxGap) {
        dot.x = prev.x - (dx / dist) * maxGap;
        dot.y = prev.y - (dy / dist) * maxGap;
      }
      place(dot);
      prev = dot;
    }

    rafId = requestAnimationFrame(tick);
  }

  function arm() {
    if (!rafId) rafId = requestAnimationFrame(tick);
  }

  document.addEventListener(
    "pointermove",
    (event) => {
      if (event.pointerType === "touch") return;
      mx = event.clientX;
      my = event.clientY;

      if (!active) {
        active = true;
        lead.x = mx;
        lead.y = my;
        place(lead);
        dots.forEach((dot) => {
          dot.x = mx;
          dot.y = my;
          place(dot);
        });
        document.documentElement.classList.add("has-custom-cursor");
        root.classList.add("is-on");
      }

      arm();
    },
    { passive: true }
  );

  document.addEventListener("pointerover", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    root.classList.toggle("is-hover", Boolean(target.closest(hoverSelector)));
  });

  document.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "touch") return;
    root.classList.add("is-down");
  });

  window.addEventListener("pointerup", () => {
    root.classList.remove("is-down");
  });

  window.addEventListener("pointercancel", () => {
    root.classList.remove("is-down");
  });

  document.documentElement.addEventListener("mouseleave", () => {
    active = false;
    root.classList.remove("is-on");
    root.classList.remove("is-down");
  });

  function teardown() {
    active = false;
    cancelAnimationFrame(rafId);
    root.remove();
    document.documentElement.classList.remove("has-custom-cursor");
  }

  finePointer.addEventListener("change", (event) => {
    if (!event.matches) teardown();
  });

  reduceMotion.addEventListener("change", (event) => {
    if (event.matches) teardown();
  });
})();
